import { API_BASE_URL } from '../config';
import { StylePresetValue } from '../constants/stylePresets';
import { ScenarioDraft, ScenarioHistoryEntry } from '../types';

export interface ChatMessagePayload {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface ProviderModel {
  id: string;
  displayName: string;
  type: 'chat' | 'image' | 'other';
}

export interface ChatRequestPayload {
  messages: ChatMessagePayload[];
  persona?: string;
  scenario?: string;
  stylePreset?: StylePresetValue;
  systemPrompt?: string;
  temperature?: number;
  maxTokens?: number;
  topP?: number;
  presencePenalty?: number;
  frequencyPenalty?: number;
  model?: string | null;
}

export interface ChatResponsePayload {
  message: ChatMessagePayload;
  usage?: {
    promptTokens?: number;
    completionTokens?: number;
    totalTokens?: number;
  };
  finishReason?: string;
}

export interface ImageRequestPayload {
  prompt: string;
  persona?: string;
  scenario?: string;
  stylePreset?: StylePresetValue;
  size?: '512x512' | '768x768' | '1024x1024' | '1536x1536';
  format?: 'b64_json' | 'url';
  quality?: 'standard' | 'hd';
  model?: string | null;
}

export interface ImageResponsePayload {
  prompt: string;
  images: Array<{
    b64Json?: string;
    url?: string;
    revisedPrompt?: string | null;
  }>;
}

export interface ProviderModelsPayload {
  models: ProviderModel[];
  defaults: {
    chat: string | null;
    image: string | null;
  };
}

export interface ScenarioBuilderRequest {
  mode: 'init' | 'answer' | 'confirm' | 'finalize';
  history: ScenarioHistoryEntry[];
  question?: string;
  answer?: string;
  scenarioDraft?: ScenarioDraft | null;
  model?: string | null;
}

export interface ScenarioBuilderResponse {
  status: 'ask' | 'confirm' | 'complete';
  question?: string;
  confirmation?: {
    prompt: string;
    suggestedEnhancement?: string | null;
  };
  scenario: ScenarioDraft;
  predefinedQuestions: string[];
  tips?: string[];
}

const withTelegramHeaders = (initDataRaw: string | null | undefined) => {
  if (!initDataRaw) {
    throw new Error('Telegram init data not ready yet.');
  }

  return {
    'Content-Type': 'application/json',
    'x-telegram-init-data': initDataRaw
  };
};

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    let details: unknown;
    try {
      details = await response.json();
    } catch {
      throw new Error(`Request failed with status ${response.status}`);
    }

    const message =
      typeof details === 'object' && details !== null && 'error' in details
        ? (details as { error: { message?: string } }).error.message ?? 'Unknown error'
        : 'Request failed';

    throw new Error(message);
  }

  return response.json() as Promise<T>;
};

export const requestChatCompletion = async (
  payload: ChatRequestPayload,
  initDataRaw: string | null | undefined
): Promise<ChatResponsePayload> => {
  const model = payload.model?.trim();
  const trimmedPayload = {
    ...payload,
    model: model && model.length > 0 ? model : undefined
  };

  const response = await fetch(`${API_BASE_URL}/api/chat`, {
    method: 'POST',
    headers: withTelegramHeaders(initDataRaw),
    body: JSON.stringify(trimmedPayload)
  });

  return handleResponse<ChatResponsePayload>(response);
};

export const requestImageGeneration = async (
  payload: ImageRequestPayload,
  initDataRaw: string | null | undefined
): Promise<ImageResponsePayload> => {
  const model = payload.model?.trim();
  const trimmedPayload = {
    ...payload,
    model: model && model.length > 0 ? model : undefined
  };

  const response = await fetch(`${API_BASE_URL}/api/image`, {
    method: 'POST',
    headers: withTelegramHeaders(initDataRaw),
    body: JSON.stringify(trimmedPayload)
  });

  return handleResponse<ImageResponsePayload>(response);
};

export const fetchProviderModels = async (
  initDataRaw: string | null | undefined
): Promise<ProviderModelsPayload> => {
  const response = await fetch(`${API_BASE_URL}/api/models`, {
    method: 'GET',
    headers: withTelegramHeaders(initDataRaw)
  });

  return handleResponse<ProviderModelsPayload>(response);
};

export const requestScenarioBuilderStep = async (
  payload: ScenarioBuilderRequest,
  initDataRaw: string | null | undefined
): Promise<ScenarioBuilderResponse> => {
  const model = payload.model?.trim();
  const trimmedPayload = {
    ...payload,
    model: model && model.length > 0 ? model : undefined
  };

  const response = await fetch(`${API_BASE_URL}/api/scenario/builder/next`, {
    method: 'POST',
    headers: withTelegramHeaders(initDataRaw),
    body: JSON.stringify(trimmedPayload)
  });

  return handleResponse<ScenarioBuilderResponse>(response);
};
